/**
 * Back in stock — capture demand on sold-out sizes.
 *
 * A greyed-out swatch tells a shopper we make her size and it is gone. This
 * turns that dead end into a signal: tap it, leave an email, and Klaviyo
 * notifies her when it returns. It also tells us which sizes to reorder.
 *
 * Posts to Klaviyo's CLIENT back-in-stock endpoint, which takes the PUBLIC company
 * ID (the same one already in the page's Klaviyo script). No private key.
 *
 * It used to post to /onsite/components/back-in-stock/subscribe. Klaviyo RETIRED that
 * endpoint: it now answers every request with 410 Gone and "This API has been retired and is
 * no longer in service." So every shopper who left her email got "That didn't go through",
 * and every one of those signals — which sizes to reorder — was lost. Nothing in the theme
 * was wrong; the API underneath it was withdrawn.
 *
 * Verified against the live store before changing anything: the old endpoint returns 410, the
 * new one returns 202 Accepted for this company id, and it sends
 * access-control-allow-origin: *, so the browser will not block it either.
 */
(function() {
  'use strict';

  var panel = document.querySelector('[data-back-in-stock]');
  if (!panel) return;

  var company = panel.getAttribute('data-klaviyo-key');
  if (!company) return;

  var emailInput = panel.querySelector('input[type="email"]');
  var sizeLabel = panel.querySelector('[data-bis-size]');
  var msg = panel.querySelector('[data-bis-msg]');
  var closeBtn = panel.querySelector('[data-bis-close]');
  var submitBtn = panel.querySelector('.BackInStock__Button');
  var marketingBox = panel.querySelector('[data-bis-marketing]');
  var marketingList = panel.getAttribute('data-klaviyo-list');

  // The theme prints the full product (with variants) as JSON already.
  var product = null;
  try {
    var node = document.querySelector('[data-product-json]');
    product = node ? JSON.parse(node.textContent).product : null;
  } catch (e) {}
  if (!product || !product.variants) return;

  var currentVariant = null;

  function selectedValues() {
    var chosen = {};
    document.querySelectorAll('.SizeSwatch__Radio:checked, .ColorSwatch__Radio:checked').forEach(function(r) {
      var pos = parseInt(r.getAttribute('data-option-position'), 10);
      if (pos) chosen[pos] = r.value;
    });
    return chosen;
  }

  /**
   * Resolve which variant the shopper means. With two size options the value
   * alone is ambiguous, so combine it with whatever else is already selected;
   * fall back to the first variant carrying that value.
   */
  function resolveVariant(position, value) {
    var chosen = selectedValues();
    chosen[position] = value;

    var exact = product.variants.filter(function(v) {
      return Object.keys(chosen).every(function(p) { return v['option' + p] === chosen[p]; });
    })[0];
    if (exact) return exact;

    return product.variants.filter(function(v) { return v['option' + position] === value; })[0] || null;
  }

  function setMessage(text, kind) {
    msg.textContent = text || '';
    msg.className = 'BackInStock__Msg' + (kind ? ' is-' + kind : '');
  }

  function open(label, variant) {
    currentVariant = variant;
    sizeLabel.textContent = label;
    setMessage('');
    emailInput.value = emailInput.value || '';
    panel.hidden = false;
    panel.classList.add('is-open');
    submitBtn.disabled = false;
    submitBtn.textContent = submitBtn.getAttribute('data-label') || 'Notify me';
    emailInput.focus({ preventScroll: true });
    panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  function close() {
    panel.classList.remove('is-open');
    panel.hidden = true;
    currentVariant = null;
    // Consent is per shopper, per moment. Carrying a tick over to the next size she looks at
    // would be consent she gave once being reused without her seeing it.
    if (marketingBox) marketingBox.checked = false;
  }

  /**
   * Join the marketing list, but only if she asked to.
   *
   * Deliberately separate from the back-in-stock subscription and deliberately not awaited:
   * she came here for a restock alert, and that must not fail or be delayed because a
   * marketing list was slow or misconfigured. Klaviyo honours the list's own single or double
   * opt-in setting, so whether she gets a confirmation email is the merchant's choice in
   * Klaviyo rather than something decided here.
   */
  function subscribeToMarketing(email) {
    if (!marketingBox || !marketingBox.checked || !marketingList) return;
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            // Recorded so the source of consent is auditable later — which is the whole point
            // of keeping a record of it.
            custom_source: 'Back in stock panel',
            /*
              Email only. NO `subscriptions` block here.

              The first version nested
              subscriptions.email.marketing.consent = 'SUBSCRIBED' inside these attributes,
              copying the shape the server-side bulk endpoint uses. This endpoint rejects it:
              "'subscriptions' is not a valid field for the resource 'profile'." So every
              opt-in 400'd — the checkbox ticked, the tick did nothing, and the profile landed
              in Klaviyo reading "Never subscribed" with a Back in Stock event and no list.

              On this endpoint the POST IS the consent: subscribing someone to a list is the
              act, and Klaviyo then honours that list's own single or double opt-in setting.
              There is nothing to declare.
            */
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: marketingList } } }
        }
      })
    }).catch(function (err) {
      if (window.console) console.error('marketing opt-in failed', err);
    });
  }

  // Sold-out swatches are disabled inputs, so the label click is ours to use.
  document.addEventListener('click', function(e) {
    var swatch = e.target.closest('.SizeSwatch--soldOut');
    if (!swatch) return;
    e.preventDefault();

    var position = parseInt(swatch.getAttribute('data-option-position'), 10);
    var value = swatch.getAttribute('data-value');
    var variant = resolveVariant(position, value);
    if (!variant) return;

    open(value, variant);
  });

  if (closeBtn) closeBtn.addEventListener('click', close);

  // Deliberately NOT a <form>: this panel sits inside the theme's product
  // form, and a nested form is invalid HTML — browsers drop the inner tag.
  function submit() {
    if (!currentVariant) return;

    var email = (emailInput.value || '').trim();
    if (!email || email.indexOf('@') < 1) {
      setMessage('Please enter a valid email address.', 'error');
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending…';
    setMessage('');

    // Fired alongside, not before: the restock alert is what she asked for and it does not
    // wait on this.
    subscribeToMarketing(email);

    /*
      Klaviyo's current API is JSON:API, not form-encoded, and it requires a dated `revision`
      header. The variant is addressed by its CATALOG id, which for the standard Shopify
      integration is the variant id namespaced as `$shopify:::$default:::<id>` — the bare
      Shopify id is not what the catalog knows it by.
    */
    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: email } } },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + currentVariant.id
            }
          }
        }
      }
    };

    fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
          encodeURIComponent(company), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify(payload)
    })
      .then(function(r) {
        /*
          A success here is 202 Accepted WITH AN EMPTY BODY, so the status decides and the body
          is only read to explain a refusal. Calling r.json() on an empty body throws, which
          the catch below would report to a shopper who is, in fact, subscribed.
        */
        if (r.status === 202 || r.ok) return { ok: true, read: true };
        return r.text().then(function(t) {
          return { ok: false, read: true, why: t.slice(0, 200) || ('HTTP ' + r.status) };
        });
      })
      .catch(function(err) {
        /*
          WE COULD NOT READ THE ANSWER — which is not the same as failing.

          `revision` is a required header and cannot be passed any other way (as a query
          parameter Klaviyo answers 400 invalid_revision), so this request is always
          preflighted, and it cannot use no-cors mode either because that strips the header.
          Klaviyo's edge does not answer that preflight consistently: called from one browser
          it returns access-control-allow-origin and the read succeeds; from another the
          response is unreadable even though the POST went through.

          And it does go through. Confirmed on this store: the profile appears in Klaviyo and
          the restock email arrives — while the panel said "That didn't go through". That is
          the worst of both, because she is subscribed AND believes she is not, so she either
          gives up or subscribes again.

          So an unreadable answer is treated as sent. It is a judgement under uncertainty and
          worth stating plainly: a readable rejection is still shown as an error, because that
          is Klaviyo actually saying no. What is assumed is only the case where the browser
          would not let us look.
        */
        return { ok: true, read: false, why: String(err).slice(0, 200) };
      })
      .then(function(res) {
        if (window.console) {
          console[res.ok ? 'log' : 'error'](
            'back-in-stock', res.read ? 'answered' : 'sent, answer unreadable', res.why || ''
          );
        }
        if (res.ok) {
          submitBtn.textContent = 'Done \u2713';
          setMessage("We'll email you the moment it's back.", 'success');
          setTimeout(close, 2600);
          return;
        }
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.getAttribute('data-label') || 'Notify me';
        setMessage("That didn't go through. Please try again.", 'error');
      });
  }

  submitBtn.addEventListener('click', submit);
  emailInput.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
  });
})();
